/**
 * The scoring recap: `summary.entries`, grouped by `periodLabel`, in server
 * order, running score on the trailing edge.
 *
 * Grouping only inserts a heading each time `periodLabel` changes from the
 * previous entry -- it never buckets-then-flattens, which would risk
 * reordering if the server ever interleaves. Entries arrive already
 * chronological; this file trusts that and never re-sorts.
 */
import type { ReactNode } from "react";

import { GameSectionCard } from "./GameSectionCard";

export type typeSummaryEntry = {
  key: string;
  periodLabel: string;
  clockLabel: string;
  teamAbbreviation: string;
  description: string;
  awayScore: number;
  homeScore: number;
};

/** The period subheader inside a card (spec 7.3): `small` 600 uppercase. */
export const SECTION_SUBHEADER_CLASS =
  "pt-2 pb-1 text-small font-semibold text-ink-secondary uppercase";

const SummaryRow = ({ entry }: { entry: typeSummaryEntry }): ReactNode => (
  <div
    className="flex min-h-touch-target items-center gap-3 border-t border-hairline first:border-t-0"
    data-testid="summary-entry"
  >
    <span className="w-13 shrink-0 text-caption text-ink-secondary tabular-nums">
      {entry.clockLabel}
    </span>
    <span className="min-w-0 flex-1 text-body text-ink">
      <span className="text-caption text-ink">{entry.teamAbbreviation}</span>{" "}
      {entry.description}
    </span>
    <span className="shrink-0 text-body-strong text-ink tabular-nums">
      {entry.awayScore}-{entry.homeScore}
    </span>
  </div>
);

export const SummaryTab = ({
  entries,
  variant = "cards",
}: {
  entries: readonly typeSummaryEntry[];
  /** `cards`: one card per period, for the Summary tab and the side column.
   *  `embedded`: period subheaders only, inside a card the caller draws. */
  variant?: "cards" | "embedded";
}): ReactNode => {
  const groups: { periodLabel: string; rows: typeSummaryEntry[] }[] = [];
  for (const entry of entries) {
    const current = groups[groups.length - 1];
    if (current?.periodLabel === entry.periodLabel) {
      current.rows.push(entry);
    } else {
      groups.push({ periodLabel: entry.periodLabel, rows: [entry] });
    }
  }

  if (variant === "embedded") {
    return (
      <div className="flex flex-col" data-testid="summary-tab">
        {groups.map((group) => (
          <div key={group.periodLabel} className="flex flex-col">
            <h4 className={SECTION_SUBHEADER_CLASS}>{group.periodLabel}</h4>
            {group.rows.map((e) => (
              <SummaryRow key={e.key} entry={e} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="summary-tab">
      {groups.map((group) => (
        <GameSectionCard key={group.periodLabel} title={group.periodLabel}>
          <div className="flex flex-col">
            {group.rows.map((e) => (
              <SummaryRow key={e.key} entry={e} />
            ))}
          </div>
        </GameSectionCard>
      ))}
    </div>
  );
};
